import React, { useEffect, useRef, useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import { ZoomIn, ZoomOut, RotateCcw, ArrowUp, ArrowDown, ArrowLeft, ArrowRight } from 'lucide-react';
// The "legacy" build is the one pdf.js recommends for bundlers like
// webpack/CRA — the default "modern" build's worker can end up loaded as an
// ES module in some webpack-bundled setups, which fails silently (the
// render() call never resolves or rejects, it just hangs) since our worker
// file is a classic script, not a module.
import * as pdfjsLib from 'pdfjs-dist/legacy/build/pdf';
import './PosterPdfViewer.css';

// Served as a static file (copied from node_modules/pdfjs-dist/legacy/build)
// so the worker loads reliably from our own origin without a
// bundler-specific worker-loader setup. Constructing the classic
// (non-module) Worker ourselves and handing it to pdf.js via `workerPort`,
// rather than just setting `workerSrc` and letting pdf.js construct it,
// avoids pdf.js sometimes trying to load it as an ES module worker in a
// webpack-bundled app.
pdfjsLib.GlobalWorkerOptions.workerPort = new Worker('/pdf.worker.min.js');

/**
 * Renders a poster PDF to a <canvas> ourselves via pdf.js, instead of
 * relying on the browser's own built-in PDF viewer inside an <iframe>.
 * That native-viewer approach silently shows a blank page on any browser
 * or embedded webview without one enabled (e.g. Chrome set to "always
 * download PDFs", some in-app browsers) — rendering the pages ourselves
 * works identically everywhere, since it's the same JS/canvas code on
 * every device.
 */
const ZOOM_MIN = 1;
const ZOOM_MAX = 3;
const ZOOM_STEP = 0.5;
const PAN_STEP = 150; // px scrolled per press of an on-screen pan button

const PosterPdfViewer = ({ url, title }) => {
  const canvasRef = useRef(null);
  const containerRef = useRef(null);
  // pdf.js throws if a second render() targets the same <canvas> while an
  // earlier one is still in flight (which React 18 Strict Mode's
  // double-invoked effects, or a fast Prev/Next click, both trigger) — track
  // the in-flight task so a new render can explicitly cancel it first.
  const renderTaskRef = useRef(null);
  const [status, setStatus] = useState('loading'); // loading | ready | error
  const [pageNum, setPageNum] = useState(1);
  const [numPages, setNumPages] = useState(1);
  // The CSS display size the canvas was rendered at, at 100% zoom — zoom
  // scales this base size rather than re-running pdf.js's render() each
  // step, so zooming in/out is instant.
  const [baseSize, setBaseSize] = useState(null);
  const [zoom, setZoom] = useState(ZOOM_MIN);

  useEffect(() => {
    let cancelled = false;

    const render = async () => {
      setStatus('loading');
      try {
        const pdfDoc = await pdfjsLib.getDocument({
          url,
          // Reference resources pdf.js needs to resolve embedded fonts/glyphs
          // some poster PDFs use — without them, those specific PDFs can fail
          // to render even though most others are fine.
          cMapUrl: '/pdfjs/cmaps/',
          cMapPacked: true,
          standardFontDataUrl: '/pdfjs/standard_fonts/',
        }).promise;
        if (cancelled) return;
        setNumPages(pdfDoc.numPages);

        const page = await pdfDoc.getPage(pageNum);
        if (cancelled) return;

        const canvas = canvasRef.current;
        const container = containerRef.current;
        if (!canvas || !container) {
          setStatus('error');
          return;
        }

        const unscaledViewport = page.getViewport({ scale: 1 });
        // A poster is typically one large A0-ish page — scale it to fill
        // the available width at a reasonably crisp resolution.
        const targetWidth = (container.clientWidth || 600) * 2;
        const scale = targetWidth / unscaledViewport.width;
        const viewport = page.getViewport({ scale });

        canvas.width = viewport.width;
        canvas.height = viewport.height;
        const displayWidth = viewport.width / 2;
        const displayHeight = viewport.height / 2;
        canvas.style.width = `${displayWidth}px`;
        canvas.style.height = `${displayHeight}px`;
        setBaseSize({ width: displayWidth, height: displayHeight });

        if (renderTaskRef.current) {
          renderTaskRef.current.cancel();
        }
        const context = canvas.getContext('2d');
        const task = page.render({ canvasContext: context, viewport });
        renderTaskRef.current = task;
        // A render that never settles (worker communication wedged, in
        // whatever way, on whatever device) must not leave someone staring
        // at "Loading poster…" forever — fail over to the "Open in new
        // tab" link after a generous timeout instead.
        const timeout = new Promise((_, reject) => {
          setTimeout(() => reject(new Error('Poster render timed out')), 12000);
        });
        await Promise.race([task.promise, timeout]);
        renderTaskRef.current = null;

        if (!cancelled) setStatus('ready');
      } catch (err) {
        // A superseded run's own render rejects one of two ways depending
        // on timing: a clean RenderingCancelledException from our own
        // .cancel() call, or — if the next run's render() call already
        // started on the same <canvas> before that cancellation was fully
        // processed — pdf.js's "same canvas" guard error. Either way this
        // is the *stale* run being superseded, not a real failure; the
        // effect that replaced it will complete and set state correctly.
        const isSupersededRun = err?.name === 'RenderingCancelledException'
          || /same canvas/i.test(err?.message || '');
        if (isSupersededRun) return;
        console.error('Failed to render poster PDF:', err);
        if (!cancelled) setStatus('error');
      }
    };

    render();
    return () => {
      cancelled = true;
      if (renderTaskRef.current) {
        renderTaskRef.current.cancel();
        renderTaskRef.current = null;
      }
    };
  }, [url, pageNum]);

  // A fresh poster (new url) always starts back on page 1, at 100% zoom.
  useEffect(() => {
    setPageNum(1);
    setZoom(ZOOM_MIN);
  }, [url]);

  // Re-apply zoom to the already-rendered canvas without re-running pdf.js —
  // scaling the CSS display size is instant, unlike a full re-render.
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || !baseSize) return;
    canvas.style.width = `${baseSize.width * zoom}px`;
    canvas.style.height = `${baseSize.height * zoom}px`;
  }, [zoom, baseSize]);

  const zoomIn = useCallback(() => {
    setZoom((z) => Math.min(ZOOM_MAX, z + ZOOM_STEP));
  }, []);
  const zoomOut = useCallback(() => {
    setZoom((z) => Math.max(ZOOM_MIN, z - ZOOM_STEP));
  }, []);
  const resetZoom = useCallback(() => {
    setZoom(ZOOM_MIN);
    containerRef.current?.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
  }, []);

  const pan = useCallback((dx, dy) => {
    containerRef.current?.scrollBy({ top: dy, left: dx, behavior: 'smooth' });
  }, []);

  const isZoomed = zoom > ZOOM_MIN;

  return (
    <div className="poster-pdf-viewer" ref={containerRef}>
      {status === 'loading' && (
        <div className="poster-pdf-status" role="status">Loading poster…</div>
      )}
      {status === 'error' && (
        <div className="poster-pdf-status poster-pdf-status--error" role="alert">
          <p>This poster couldn&apos;t be displayed here.</p>
          {/* Only shown when in-app rendering genuinely fails — there's no
              other way to see the poster at all in that case. This is
              deliberately not offered on the normal success path (see
              PosterPresentation.js), since leaving the app is a dead end
              for AAC/switch-scanning users: a native-viewer new tab has no
              reliable way back into the app's own navigation state. */}
          <a href={url} target="_blank" rel="noopener noreferrer" className="btn btn-outline">
            Open the poster in a new tab instead
          </a>
        </div>
      )}
      <canvas
        ref={canvasRef}
        className="poster-pdf-canvas"
        style={{ display: status === 'ready' ? 'block' : 'none' }}
        role="img"
        aria-label={`${title} — poster page ${pageNum} of ${numPages}, zoomed to ${Math.round(zoom * 100)} percent`}
      />
      {status === 'ready' && (
        <div className="poster-pdf-controls">
          {numPages > 1 && (
            <div className="poster-pdf-pages">
              <button
                type="button"
                className="btn btn-outline"
                onClick={() => setPageNum((p) => Math.max(1, p - 1))}
                disabled={pageNum <= 1}
              >
                Previous page
              </button>
              <span>Page {pageNum} of {numPages}</span>
              <button
                type="button"
                className="btn btn-outline"
                onClick={() => setPageNum((p) => Math.min(numPages, p + 1))}
                disabled={pageNum >= numPages}
              >
                Next page
              </button>
            </div>
          )}

          <div className="poster-pdf-zoom" role="group" aria-label="Zoom controls">
            <button type="button" className="btn btn-outline" onClick={zoomOut} disabled={zoom <= ZOOM_MIN} aria-label="Zoom out">
              <ZoomOut size={18} aria-hidden="true" />
            </button>
            <span aria-live="polite">{Math.round(zoom * 100)}%</span>
            <button type="button" className="btn btn-outline" onClick={zoomIn} disabled={zoom >= ZOOM_MAX} aria-label="Zoom in">
              <ZoomIn size={18} aria-hidden="true" />
            </button>
            <button type="button" className="btn btn-outline" onClick={resetZoom} disabled={!isZoomed} aria-label="Reset zoom">
              <RotateCcw size={18} aria-hidden="true" />
            </button>
          </div>

          {isZoomed && (
            <div className="poster-pdf-pan" role="group" aria-label="Pan the zoomed poster">
              <button type="button" className="btn btn-outline" onClick={() => pan(0, -PAN_STEP)} aria-label="Pan up">
                <ArrowUp size={18} aria-hidden="true" />
              </button>
              <button type="button" className="btn btn-outline" onClick={() => pan(-PAN_STEP, 0)} aria-label="Pan left">
                <ArrowLeft size={18} aria-hidden="true" />
              </button>
              <button type="button" className="btn btn-outline" onClick={() => pan(PAN_STEP, 0)} aria-label="Pan right">
                <ArrowRight size={18} aria-hidden="true" />
              </button>
              <button type="button" className="btn btn-outline" onClick={() => pan(0, PAN_STEP)} aria-label="Pan down">
                <ArrowDown size={18} aria-hidden="true" />
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

PosterPdfViewer.propTypes = {
  url: PropTypes.string.isRequired,
  title: PropTypes.string.isRequired,
};

export default PosterPdfViewer;
